import {
	type BackofficeAuthExportsIdResponse,
	backofficeAuthExportsId
} from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { PictogramEnded } from '@repo/ui/components/pictograms/ended'
import { PictogramFatalError } from '@repo/ui/components/pictograms/fatal-error'
import { PictogramSuccess } from '@repo/ui/components/pictograms/success'
import { PictogramTiming } from '@repo/ui/components/pictograms/timing'
import { makeBreadcrumbFromCache } from '@repo/ui/components/query-breadcrumbs'
import { formatLocaleDate } from '@repo/ui/dates'
import { startDownload } from '@repo/ui/download'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@repo/ui/empty'
import { Spinner } from '@repo/ui/spinner'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download, RefreshCw } from 'lucide-react'
import { type ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'

function exportDetailQueryOptions(id: string) {
	return queryOptions({
		queryKey: makeQueryKeys.exports.detail(id),
		queryFn: async (queryContext) => {
			const response = await backofficeAuthExportsId({
				path: { id },
				signal: queryContext.signal
			})
			return response.data
		}
	})
}

export const Route = createFileRoute('/(private)/exports/$id/')({
	component: AdminExportDetail,
	staticData: {
		breadcrumb: ({ params, queryClient }) =>
			makeBreadcrumbFromCache<BackofficeAuthExportsIdResponse['data']>(
				queryClient,
				makeQueryKeys.exports.detail(params.id),
				(data) => data.fileName
			)
	}
})

function AdminExportDetail() {
	const { t } = useTranslation()
	const routeParams = Route.useParams()
	const exportQuery = useQuery(exportDetailQueryOptions(routeParams.id))

	useAutoDownload(exportQuery.data?.downloadUrl)

	if (exportQuery.error) {
		throw exportQuery.error
	}

	return (
		<div className='flex h-full min-h-0 flex-col justify-center overflow-auto p-6'>
			<div className='mx-auto w-full max-w-lg'>
				{exportQuery.isPending && (
					<Empty className='border-none'>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<Spinner />
							</EmptyMedia>
							<EmptyTitle>
								{t('admin-exports:loading')}
							</EmptyTitle>
						</EmptyHeader>
					</Empty>
				)}
				{exportQuery.data && (
					<ExportSummary
						exportDetail={exportQuery.data}
						onRefresh={() => exportQuery.refetch()}
						refreshing={exportQuery.isFetching}
					/>
				)}
			</div>
		</div>
	)
}

function useAutoDownload(downloadUrl: string | null | undefined) {
	const downloadedUrlRef = useRef<string | null>(null)

	useEffect(() => {
		if (!downloadUrl || downloadedUrlRef.current === downloadUrl) {
			return
		}

		downloadedUrlRef.current = downloadUrl
		startDownload(downloadUrl)
	}, [downloadUrl])
}

type ExportStatus = BackofficeAuthExportsIdResponse['data']['status']

interface ExportSummaryConfig {
	titleKey: 'waiting' | 'completed' | 'failed' | 'expired'
	media: ReactNode
}

const EXPORT_SUMMARY_CONFIG = {
	pending: { titleKey: 'waiting', media: <PictogramTiming /> },
	running: { titleKey: 'waiting', media: <PictogramTiming /> },
	completed: { titleKey: 'completed', media: <PictogramSuccess /> },
	failed: { titleKey: 'failed', media: <PictogramFatalError /> },
	expired: { titleKey: 'expired', media: <PictogramEnded /> }
} as const satisfies Record<ExportStatus, ExportSummaryConfig>

function ExportSummary({
	exportDetail,
	onRefresh,
	refreshing
}: {
	exportDetail: BackofficeAuthExportsIdResponse['data']
	onRefresh: () => void
	refreshing: boolean
}) {
	const { t } = useTranslation()
	const { status, downloadUrl, errorMessage, expiresAt } = exportDetail
	const config = EXPORT_SUMMARY_CONFIG[status]
	const isInProgress = status === 'pending' || status === 'running'
	const isCompleted = status === 'completed'

	const description =
		status === 'failed'
			? (errorMessage ?? t('admin-exports:failed.description'))
			: t(`admin-exports:${config.titleKey}.description`)

	return (
		<Empty className='border-none'>
			<EmptyHeader className='max-w-md'>
				<EmptyMedia>{config.media}</EmptyMedia>
				<EmptyTitle>
					{t(`admin-exports:${config.titleKey}.title`)}
				</EmptyTitle>
				<EmptyDescription>{description}</EmptyDescription>
			</EmptyHeader>
			{(downloadUrl || isInProgress || isCompleted) && (
				<EmptyContent className='max-w-md'>
					{(downloadUrl || isInProgress) && (
						<div className='flex flex-wrap items-center justify-center gap-2'>
							{downloadUrl && (
								<Button
									onClick={() => startDownload(downloadUrl)}
									type='button'
								>
									<Download />
									{t('admin-exports:download_again')}
								</Button>
							)}
							{isInProgress && (
								<Button
									disabled={refreshing}
									onClick={onRefresh}
									type='button'
									variant='outline'
								>
									<RefreshCw />
									{t('admin-exports:refresh')}
								</Button>
							)}
						</div>
					)}
					{isCompleted && (
						<p className='text-muted-foreground text-xs'>
							{t('admin-exports:details.expires_at')}{' '}
							{formatLocaleDate(expiresAt, 'Pp')}
						</p>
					)}
				</EmptyContent>
			)}
		</Empty>
	)
}
